/**
 * i18n facade — exposes the active-language string table via React hook.
 *
 * Pattern:
 *   const t = useT();
 *   return <h1>{t.app.title}</h1>;
 *
 * The `lang` state is a tiny Zustand store persisted to localStorage so
 * the picked language survives reloads + character-switches. Components
 * subscribe through the hook, so a `setLang('en')` call propagates to
 * every consumer in one render cycle.
 *
 * For non-component code that needs a one-off translation (rare; mostly
 * domain layer should be language-agnostic and surface keys instead),
 * `getT()` returns the current table imperatively.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { de } from './de';
import { en } from './en';

export type Lang = 'de' | 'en';

interface LangState {
  lang: Lang;
  setLang: (lang: Lang) => void;
}

export const useLangStore = create<LangState>()(
  persist(
    (set) => ({
      lang: 'de',
      setLang: (lang) => set({ lang }),
    }),
    { name: 'cve.lang.v1' },
  ),
);

/** All known language tables. Keys must match the `Lang` union.
 *  The `en` table is cast to `typeof de` — its `as const` literals
 *  differ from the German ones, but TS still structurally verifies the
 *  shape (same keys, same nesting) at the original `en.ts` definition.
 *  Consumers see the German literal types, which is fine: they only
 *  read the keys, not compare string values. */
const TABLES: Record<Lang, typeof de> = { de, en: en as unknown as typeof de };

/** React hook — re-subscribes when language changes. Use this in components. */
export function useT(): typeof de {
  const lang = useLangStore((s) => s.lang);
  return TABLES[lang];
}

/** Imperative getter for non-component code. Avoid when possible — most
 *  string literals belong in components where useT() is reactive. */
export function getT(): typeof de {
  return TABLES[useLangStore.getState().lang];
}

/** Public list of supported languages with their display labels — used
 *  by the Settings language-picker so adding a new language is a single
 *  TABLES + LANG_OPTIONS entry. */
export const LANG_OPTIONS: ReadonlyArray<{ code: Lang; label: string }> = [
  { code: 'de', label: 'Deutsch' },
  { code: 'en', label: 'English' },
];

// ── Lookup helpers ────────────────────────────────────────────
// These take a key + read from the active language table. Used by
// components that need to translate an item / vault / enemy / resource
// id at render-time. Reactive enough for normal play (every render calls
// them fresh); memoised lists pick up the new strings on the next
// natural re-render after a setLang() call.

export function itemName(nameKey: string): string {
  const table = getT().items as Record<string, string>;
  return table[nameKey] ?? nameKey;
}

export function rarityLabel(rarity: keyof typeof de.inventory.rarity): string {
  return getT().inventory.rarity[rarity];
}

export function enemyName(id: string): string {
  const table = getT().dungeon.enemies as Record<string, string>;
  return table[id] ?? id;
}

export function vaultName(id: string): string {
  const table = getT().dungeon.vaults as Record<string, string>;
  return table[id] ?? id;
}

export function vaultDesc(id: string): string {
  const descKey = `${id}_desc`;
  const table = getT().dungeon.vaults as Record<string, string>;
  return table[descKey] ?? '';
}

export function resourceLabel(id: string): string {
  const table = getT().resources as Record<string, string>;
  return table[id] ?? id;
}

export function minerName(id: string): string {
  const table = getT().miners as Record<string, string>;
  return table[id] ?? id;
}

export function minerDesc(id: string): string {
  const key = `${id}_desc`;
  const table = getT().miners as Record<string, string>;
  return table[key] ?? '';
}

export function boostName(id: string): string {
  const table = getT().boosts as Record<string, string>;
  return table[id] ?? id;
}

export function boostDesc(id: string): string {
  const key = `${id}_desc`;
  const table = getT().boosts as Record<string, string>;
  return table[key] ?? '';
}

export type I18n = typeof de;
